import React from "react";
import { OrderPaymentTransactionResponse } from "@/api";

export interface PaymentTransactionsTableProps {
  transactions?: OrderPaymentTransactionResponse[];

  onEdit?: (transaction: OrderPaymentTransactionResponse) => void;
}

export default function PaymentTransactionsTable({
  transactions = [],
  onEdit,
}: PaymentTransactionsTableProps) {
  const money = (value?: number) =>
    `₹${Number(value ?? 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const formatDate = (value?: string) => {
    if (!value) {
      return "-";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b bg-gray-50">
            <th className="px-4 py-3 text-left font-medium">
              Transaction #
            </th>

            <th className="px-4 py-3 text-left font-medium">
              Date
            </th>

            <th className="px-4 py-3 text-left font-medium">
              Type
            </th>

            <th className="px-4 py-3 text-right font-medium">
              Amount
            </th>

            <th className="px-4 py-3 text-left font-medium">
              Reason
            </th>

            <th className="px-4 py-3 text-left font-medium">
              Reference
            </th>

            {onEdit && (
              <th className="px-4 py-3 text-right font-medium">
                Actions
              </th>
            )}
          </tr>
        </thead>

        <tbody>
          {transactions.length > 0 ? (
            transactions.map((transaction) => (
              <tr
                key={transaction.id}
                className="border-b last:border-0"
              >
                {/* Transaction */}
                <td className="px-4 py-3 font-medium">
                  #{transaction.id}
                </td>

                {/* Date */}
                <td className="px-4 py-3">
                  {formatDate(
                    transaction.transaction_date
                  )}
                </td>

                {/* Type */}
                <td className="px-4 py-3">
                  {transaction.transaction_type ?? "-"}
                </td>

                {/* Amount */}
                <td className="px-4 py-3 text-right font-medium">
                  {money(
                    Number(
                      transaction.transaction_amount
                    )
                  )}
                </td>

                {/* Reason */}
                <td className="px-4 py-3">
                  {transaction.transaction_reason ?? "-"}
                </td>

                {/* Reference */}
                <td className="px-4 py-3">
                  {transaction.transaction_reference
                    ? JSON.stringify(
                        transaction.transaction_reference
                      )
                    : "-"}
                </td>

                {/* Actions */}
                {onEdit && (
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() =>
                        onEdit(transaction)
                      }
                      className="rounded-md border px-3 py-2 text-sm"
                    >
                      Edit
                    </button>
                  </td>
                )}
              </tr>
            ))
          ) : (
            /*
             * Empty-state placeholder.
             *
             * Deliberately render one empty row rather than
             * replacing the table with an empty-state card.
             */
            <tr className="border-b">
              <td className="px-4 py-6 text-gray-400">
                —
              </td>

              <td className="px-4 py-6 text-gray-400">
                —
              </td>

              <td className="px-4 py-6 text-gray-400">
                —
              </td>

              <td className="px-4 py-6 text-right text-gray-400">
                —
              </td>

              <td className="px-4 py-6 text-gray-400">
                —
              </td>

              <td className="px-4 py-6 text-gray-400">
                —
              </td>

              {onEdit && (
                <td className="px-4 py-6 text-right text-gray-400">
                  —
                </td>
              )}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}